import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useAuthStore } from './stores/authStore';
import { useLearnerStore } from './stores/learnerStore';
import { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { useAccess } from './hooks/useAccess';
import { useMe } from './hooks/useMe';
import { useLearner } from './hooks/useLearner';
import { getOnboardingResume } from './api/onboarding';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import BookshelfPage from './pages/BookshelfPage';
import LibraryPage from './pages/library/LibraryPage';
import SeriesDetailPage from './pages/library/SeriesDetailPage';
import LanguagePage from './pages/onboarding/LanguagePage';
import UseCasePage from './pages/onboarding/UseCasePage';
import ChildProfilePage from './pages/onboarding/ChildProfilePage';
import CurriculumFineTunePage from './pages/onboarding/CurriculumFineTunePage';
import WelcomePage from './pages/onboarding/WelcomePage';
import PlacementBandPage from './pages/onboarding/PlacementBandPage';
import PlacementReviewPage from './pages/placement/PlacementReviewPage';
import BuddyPage from './pages/onboarding/BuddyPage';
import GeneratingPage from './pages/story/GeneratingPage';
import ReaderPage from './pages/story/ReaderPage';
import CelebrationPage from './pages/story/CelebrationPage';
import StoryReviewPage from './pages/story/StoryReviewPage';
import WorkbookPage from './pages/workbook/WorkbookPage';
import CharacterProfilePage from './pages/workbook/CharacterProfilePage';
import CharacterSelectPage from './pages/workbook/CharacterSelectPage';
import WorksheetPreviewPage from './pages/workbook/WorksheetPreviewPage';
import PracticePage from './pages/practice/PracticePage';
import MapPage from './pages/map/MapPage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import SwitchCurriculumPage from './pages/SwitchCurriculumPage';
import TopicPickerPage from './pages/TopicPickerPage';
import NotFoundPage from './pages/NotFoundPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPage from './pages/admin/AdminPage';
// Code-split: the Map Atlas editor (admin-only) is lazy-loaded so non-admin
// users never download it (BP-MAP-ATLAS-03).
const AtlasEditorPage = lazy(() => import('./pages/admin/AtlasEditorPage'));
import ErrorBoundary from './components/common/ErrorBoundary';
import AppLoadingScreen from './components/common/AppLoadingScreen';
import { DelightProvider } from './contexts/DelightContext';
import DelightLayer from './components/delight/DelightLayer';
import i18n from './i18n';
import styles from './App.module.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,   // 10 min — data considered fresh
      gcTime: 15 * 60 * 1000,       // 15 min — keep in cache before GC
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user) return <div className={styles.loadingScreen}>Loading...</div>;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

// Redirect unauthenticated users to login.
// Redirect authenticated users who haven't completed onboarding to the correct step.
// Uses activeLearner.onboarding_step (read via useLearner from the ['learners']
// React Query) for deterministic routing.
function OnboardingGuard({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: activeLearner, isLoading: learnersLoading } = useLearner();
  const location = useLocation();
  const { data: me, isLoading } = useMe();

  // Active + unplaced learner: ask the backend where to resume. Hooks must
  // always run, so gate via `enabled` rather than wrapping the call.
  const isActiveUnplaced =
    !!me &&
    !me.onboarding_complete &&
    !me.is_admin &&
    activeLearner?.status === 'active';

  const { data: resume, isLoading: resumeLoading } = useQuery({
    queryKey: ['onboarding', 'resume'],
    queryFn: getOnboardingResume,
    enabled: isActiveUnplaced,
    staleTime: 0,
    retry: 1,
  });

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isLoading || !me || learnersLoading) return <AppLoadingScreen />;

  // BP-ONBOARDING-ATOMICITY-02: admin accounts bypass the onboarding gate
  // unconditionally — admins are not learners and must never be blocked.
  if (me.is_admin) return children;

  if (!me.onboarding_complete) {
    // Language gate (BP-ONBOARDING-LANGUAGE-STEP-01) — fires BEFORE both the
    // active-unplaced and draft branches. Marker-based, orthogonal to
    // onboarding_step: if the user hasn't yet made an explicit language choice,
    // send them to the Select Language screen first. Loop-prevention: if we're
    // already on that route, render children (mirrors the resume-branch pattern
    // below). LanguagePage sets the marker then routes to child-profile.
    if (!me.onboarding_language_chosen_at) {
      if (location.pathname === '/onboarding/language') return children;
      return <Navigate to="/onboarding/language" replace />;
    }

    // Active + unplaced — wait for resume to resolve, then route there.
    if (isActiveUnplaced) {
      if (resumeLoading || !resume) return <AppLoadingScreen />;
      const nextRoute = resume.nextRoute || '/onboarding/placement-band';
      const nextPath = nextRoute.split('?')[0];
      // Loop prevention: if we're already at the resume destination, render
      // children (e.g. the eval story reader) instead of redirecting back to
      // it — that would cycle.
      if (location.pathname === nextPath) return children;
      return <Navigate to={nextRoute} replace />;
    }

    // Draft path — unchanged. Map onboarding_step to the next page.
    const step = activeLearner?.status === 'draft'
      ? (activeLearner.onboarding_step || 0)
      : 0;

    if (step >= 3) return <Navigate to="/onboarding/confirm" replace />;
    if (step >= 2) return <Navigate to="/onboarding/use-case" replace />;
    if (step >= 1) return <Navigate to="/onboarding/buddy" replace />;
    return <Navigate to="/onboarding/child-profile" replace />;
  }

  return children;
}

// Packet A4: entitlement is no longer persisted in Zustand. useAccess()
// reads /users/me/access on demand (staleTime 30s, refetch on focus).
// While the query is pending we render a loading screen — NEVER redirect
// on idle/loading, since that would briefly show an expired user the
// billing page on every page load.
function SubscriptionGuard({ children }) {
  const { data: access, status } = useAccess();
  if (status === 'pending' || !access) return <AppLoadingScreen />;
  if (access.has_access === false) return <Navigate to="/account/billing" replace />;
  return children;
}

// Restores the user's saved app_language on every session boot. Without
// this, a Chinese-language user sees English until they visit Settings,
// because the only other caller of i18n.changeLanguage() is getSettings().
function LanguageRestorer() {
  const { data: me } = useMe();
  useEffect(() => {
    if (me?.app_language && i18n.language !== me.app_language) {
      i18n.changeLanguage(me.app_language);
    }
  }, [me?.app_language]);
  return null;
}

// Onboarding-only route: authenticated but NOT yet onboarded.
// If onboarding is already complete (derived from learner state via /users/me),
// send to dashboard.
function OnboardingRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: me } = useMe();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (me?.onboarding_complete) return <Navigate to="/" replace />;
  return children;
}

function App() {
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const [appReady, setAppReady] = useState(!localStorage.getItem('access_token'));
  const [initError, setInitError] = useState(null);

  const initApp = useCallback(async () => {
    if (!localStorage.getItem('access_token')) return;
    setInitError(null);
    try {
      // Step 1: establish identity first
      await fetchUser();

      // Step 2: if the persisted activeLearnerId belongs to a different user,
      // drop it before useLearners() resolves — otherwise useLearner() could
      // return a stale "selected" id that no longer exists for this user.
      // FP-LEARNER-STORE-RETIREMENT-01 narrowed the persisted blob to just
      // userId + activeLearnerId, so the legacy-unstamped check is no longer
      // needed — the linguapals-learner-v2 key bump already evicts older
      // blobs that contained learners[] / activeLearner.
      const currentUser = useAuthStore.getState().user;
      const persistedUserId = useLearnerStore.getState().userId;
      if (persistedUserId && currentUser?.id && persistedUserId !== currentUser.id) {
        useLearnerStore.getState().clearLearners();
      }

      // Step 3: stamp the cache with the current user's identity. Learner
      // data is fetched by useLearners() inside components — no imperative
      // pre-fetch here. OnboardingGuard waits on its own learnersLoading
      // state before routing.
      if (currentUser?.id) {
        useLearnerStore.getState().setUserId(currentUser.id);
      }
    } catch (err) {
      const isAuthenticated = useAuthStore.getState().isAuthenticated;
      if (isAuthenticated) {
        setInitError('Unable to connect. Please check your internet and try again.');
      }
    } finally {
      setAppReady(true);
    }
  }, [fetchUser]);

  useEffect(() => {
    initApp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!appReady) {
    return <AppLoadingScreen />;
  }

  if (initError) {
    return (
      <AppLoadingScreen>
        <p className={styles.initErrorMessage}>
          {initError}
        </p>
        <button
          type="button"
          onClick={() => { setAppReady(false); setInitError(null); initApp().finally(() => setAppReady(true)); }}
          className={styles.retryButton}
        >
          Try Again
        </button>
      </AppLoadingScreen>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageRestorer />
      <DelightProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Onboarding routes (protected, onboarding-only) */}
          <Route
            path="/onboarding/language"
            element={
              <OnboardingRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <LanguagePage />
                </ErrorBoundary>
              </OnboardingRoute>
            }
          />
          <Route
            path="/onboarding/use-case"
            element={
              <OnboardingRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <UseCasePage />
                </ErrorBoundary>
              </OnboardingRoute>
            }
          />
          <Route
            path="/onboarding/child-profile"
            element={
              <OnboardingRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <ChildProfilePage />
                </ErrorBoundary>
              </OnboardingRoute>
            }
          />
          <Route
            path="/onboarding/buddy"
            element={
              <OnboardingRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <BuddyPage />
                </ErrorBoundary>
              </OnboardingRoute>
            }
          />
          {/* Add-learner route — accessible to already-onboarded users */}
          <Route
            path="/settings/add-learner"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Something went wrong. Tap to try again.">
                  <ChildProfilePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/confirm"
            element={
              <OnboardingRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <CurriculumFineTunePage />
                </ErrorBoundary>
              </OnboardingRoute>
            }
          />

          <Route
            path="/onboarding/placement-band"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <PlacementBandPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          <Route
            path="/onboarding/welcome"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Something went wrong during setup. Tap to try again.">
                  <WelcomePage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          <Route
            path="/placement/review"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Something went wrong while reviewing the story. Tap to try again.">
                  <PlacementReviewPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Story routes (protected) */}
          <Route
            path="/story/topic-picker"
            element={
              <OnboardingGuard>
                <SubscriptionGuard>
                  <ErrorBoundary fallbackMessage="We couldn't load the topic picker. Tap to try again.">
                    <TopicPickerPage />
                  </ErrorBoundary>
                </SubscriptionGuard>
              </OnboardingGuard>
            }
          />
          <Route
            path="/story/generating"
            element={
              <OnboardingGuard>
                <SubscriptionGuard>
                  <ErrorBoundary fallbackMessage="Story generation hit a snag. Tap to try again.">
                    <GeneratingPage />
                  </ErrorBoundary>
                </SubscriptionGuard>
              </OnboardingGuard>
            }
          />
          <Route
            path="/story/read/:storyId"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="We couldn't load your story. Tap to retry.">
                  <ReaderPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />
          <Route
            path="/story/celebrate/:storyId"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="We couldn't load the celebration. Tap to retry.">
                  <CelebrationPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* Bookshelf (protected) */}
          <Route
            path="/bookshelf"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Couldn't load your bookshelf. Tap to refresh.">
                  <BookshelfPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* Library (pre-written stories) */}
          <Route
            path="/library"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Couldn't load the Library. Tap to refresh.">
                  <LibraryPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />
          <Route
            path="/library/:seriesId"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Couldn't load this series. Tap to refresh.">
                  <SeriesDetailPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* Workbook routes (protected) */}
          <Route
            path="/workbook"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Workbook failed to load. Tap to try again.">
                  <WorkbookPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          >
            <Route
              path="character/:char"
              element={
                <OnboardingGuard>
                  <ErrorBoundary fallbackMessage="Couldn't load this character. Tap to try again.">
                    <CharacterProfilePage />
                  </ErrorBoundary>
                </OnboardingGuard>
              }
            />
          </Route>
          <Route
            path="/workbook/select-characters"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Couldn't load character selection. Tap to try again.">
                  <CharacterSelectPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />
          <Route
            path="/workbook/preview/:worksheetId"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Worksheet preview failed to load. Tap to try again.">
                  <WorksheetPreviewPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* Practice (protected) — accessible during trial, no SubscriptionGuard */}
          <Route
            path="/practice"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Practice failed to load. Tap to try again.">
                  <PracticePage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* /map redirects to / — ADR-047 made the map the home screen */}
          <Route path="/map" element={<Navigate to="/" replace />} />

          {/* Story review (protected) */}
          <Route
            path="/story/review/:storyId"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="We couldn't load the story review. Tap to retry.">
                  <StoryReviewPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* Billing (protected) */}
          <Route
            path="/account/billing"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Couldn't load billing details. Tap to try again.">
                  <BillingPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="/subscription" element={<Navigate to="/account/billing" replace />} />

          {/* Settings (protected) */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Settings failed to load. Tap to try again.">
                  <SettingsPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Curriculum switch (protected) — post-onboarding */}
          <Route
            path="/settings/curriculum"
            element={
              <ProtectedRoute>
                <ErrorBoundary fallbackMessage="Couldn't open the curriculum switcher. Tap to try again.">
                  <SwitchCurriculumPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Profile redirects to Settings */}
          <Route
            path="/profile"
            element={<Navigate to="/settings" replace />}
          />

          {/* Admin (protected, admin-only) */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <ErrorBoundary fallbackMessage="Admin panel failed to load. Tap to try again.">
                  <AdminPage />
                </ErrorBoundary>
              </AdminRoute>
            }
          />

          {/* Map Atlas editor (protected, admin-only, lazy) — BP-MAP-ATLAS-03 */}
          <Route
            path="/admin/map-atlas-editor"
            element={
              <AdminRoute>
                <ErrorBoundary fallbackMessage="Atlas editor failed to load. Tap to try again.">
                  <Suspense fallback={<AppLoadingScreen />}>
                    <AtlasEditorPage />
                  </Suspense>
                </ErrorBoundary>
              </AdminRoute>
            }
          />

          {/* Map home screen (protected, default route) — ADR-047 */}
          <Route
            path="/"
            element={
              <OnboardingGuard>
                <ErrorBoundary fallbackMessage="Map failed to load. Tap to try again.">
                  <MapPage />
                </ErrorBoundary>
              </OnboardingGuard>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <DelightLayer />
      </ErrorBoundary>
      </DelightProvider>
    </QueryClientProvider>
  );
}

export default App;
